import * as React from 'react';
import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useActivation from '@/src/Activations/components/hooks/useActivation';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import useWhitelabelSettings from '@/src/WhitelabelSettings/components/hooks/useWhitelabelSettings';
import useShare from '@/src/Share/components/hooks/useShare';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { dedupeSessions, dedupeShares } from '../lib/eventStats';

/*
 * EventContext — one resolution per activation (IA audit 2026-08-14:
 * six pages independently re-fetched the first gallery and org domain
 * and re-implemented the stats, with three disagreeing definitions).
 * The chrome resolves the LIGHT facts eagerly — activation, first
 * gallery, org gallery domain, capabilities — and the heavy collections
 * (sessions, shares, media) lazily through promise-cached ensure*()
 * calls, so Overview and Media share one fetch while pages that never
 * need media never pay for it. The cache lives for the life of the
 * event chrome: navigating Overview → Media reuses it.
 */

export interface EventCapabilities {
    hasGallery: boolean;
    sharingOn: boolean;
    crowdOn: boolean;
    hasDevices: boolean;
}

export interface EventContextValue {
    assignedDeviceCount: number;
    assignedGroupCount: number;
    activationId: number;
    activation: any | null;
    gallery: any | null;
    galleryId: number | null;
    gallerySlug: string | null;
    /* org gallery domain, e.g. https://smlbth.io */
    domain: string;
    capabilities: EventCapabilities;
    resolved: boolean;
    refreshGallery: () => Promise<void>;
    ensureSessions: () => Promise<any[]>;
    ensureShares: () => Promise<any[]>;
    ensureMedia: () => Promise<any[]>;
    /* populated after the matching ensure*() resolves */
    sessions: any[] | null;
    shares: any[] | null;
    media: any[] | null;
    invalidateMedia: () => void;
}

const Ctx = createContext<EventContextValue | null>(null);

export function useEventContext(): EventContextValue {
    const value = useContext(Ctx);
    if (!value)
        throw new Error(
            'useEventContext outside EventContextProvider (event chrome)',
        );
    return value;
}

export function EventContextProvider({
    activationId,
    children,
}: {
    activationId: number;
    children: React.ReactNode;
}) {
    const { orgId, org } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();
    const { getActivation, getActivationSessions, getActivationPhotos } =
        useActivation();
    const { getGalleriesForActivation } = useGetGalleries();
    const { getSharesForActivation } = useShare();
    const { getOrgDomain } = useWhitelabelSettings();

    const [activation, setActivation] = useState<any | null>(null);
    const [gallery, setGallery] = useState<any | null>(null);
    const [domain, setDomain] = useState('');
    const [hasDevices, setHasDevices] = useState(false);
    const [assignedDeviceCount, setAssignedDeviceCount] = useState(0);
    const [assignedGroupCount, setAssignedGroupCount] = useState(0);
    const [resolved, setResolved] = useState(false);
    const [sessions, setSessions] = useState<any[] | null>(null);
    const [shares, setShares] = useState<any[] | null>(null);
    const [media, setMedia] = useState<any[] | null>(null);

    /* promise caches so concurrent ensure*() calls share one request */
    const promises = useRef<{
        sessions?: Promise<any[]>;
        shares?: Promise<any[]>;
        media?: Promise<any[]>;
    }>({});

    useEffect(() => {
        let mounted = true;
        promises.current = {};
        setSessions(null);
        setShares(null);
        setMedia(null);
        setResolved(false);
        (async () => {
            const [act, galleries, base] = await Promise.all([
                getActivation(activationId).catch(() => null),
                getGalleriesForActivation(activationId).catch(() => []),
                getOrgDomain(org?.id).catch(() => ''),
            ]);
            if (!mounted) return;
            setActivation(act);
            const first = galleries?.[0] ?? null;
            setGallery(first);
            setDomain(base ?? '');
            if (first?.id != null) {
                const [devRows, grpRows] = await Promise.all([
                    authenticatedApi
                        .get(
                            `/device-management/assignments/gallery/${first.id}`,
                        )
                        .then((r) => r.data)
                        .catch(() => []),
                    authenticatedApi
                        .get(
                            `/${orgId}/device-groups/assignments/gallery/${first.id}`,
                        )
                        .then((r) => r.data)
                        .catch(() => []),
                ]);
                if (!mounted) return;
                setAssignedDeviceCount(devRows?.length ?? 0);
                setAssignedGroupCount(grpRows?.length ?? 0);
                setHasDevices(
                    (devRows?.length ?? 0) > 0 ||
                        (grpRows?.length ?? 0) > 0,
                );
            }
            if (mounted) setResolved(true);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId, orgId]);

    const refreshGallery = async () => {
        const galleries = await getGalleriesForActivation(
            activationId,
        ).catch(() => []);
        setGallery(galleries?.[0] ?? null);
    };

    const ensureSessions = () => {
        if (!promises.current.sessions) {
            promises.current.sessions = getActivationSessions(activationId)
                .then((rows: any[]) => {
                    const uniq = dedupeSessions(rows);
                    setSessions(uniq);
                    return uniq;
                })
                .catch(() => {
                    promises.current.sessions = undefined;
                    return [];
                });
        }
        return promises.current.sessions;
    };

    const ensureShares = () => {
        if (!promises.current.shares) {
            promises.current.shares = getSharesForActivation(activationId)
                .then((rows: any[]) => {
                    const uniq = dedupeShares(rows);
                    setShares(uniq);
                    return uniq;
                })
                .catch(() => {
                    promises.current.shares = undefined;
                    return [];
                });
        }
        return promises.current.shares;
    };

    const ensureMedia = () => {
        if (!promises.current.media) {
            promises.current.media = getActivationPhotos(activationId, [])
                .then((rows: any[]) => {
                    setMedia(rows ?? []);
                    return rows ?? [];
                })
                .catch(() => {
                    promises.current.media = undefined;
                    return [];
                });
        }
        return promises.current.media;
    };

    const invalidateMedia = () => {
        promises.current.media = undefined;
        setMedia(null);
    };

    const capabilities = useMemo<EventCapabilities>(
        () => ({
            hasGallery: gallery != null,
            sharingOn: !!gallery?.emailEnabled || !!gallery?.textEnabled,
            crowdOn: !!gallery?.crowdEnabled,
            hasDevices,
        }),
        [gallery, hasDevices],
    );

    const value = useMemo<EventContextValue>(
        () => ({
            activationId,
            assignedDeviceCount,
            assignedGroupCount,
            activation,
            gallery,
            galleryId: gallery?.id ?? null,
            gallerySlug: gallery?.slug ?? null,
            domain,
            capabilities,
            resolved,
            refreshGallery,
            ensureSessions,
            ensureShares,
            ensureMedia,
            sessions,
            shares,
            media,
            invalidateMedia,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            activationId,
            assignedDeviceCount,
            assignedGroupCount,
            activation,
            gallery,
            domain,
            capabilities,
            resolved,
            sessions,
            shares,
            media,
        ],
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
